import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import get from 'lodash/get';
import { propStyles } from '../utils/propStyles';
import Autocomplete from './Autocomplete';
import Disclaimers from './Disclaimers';
import Translate from './Translate';
import ChartBarChart from './ChartBarChart';
import ChartLineChart from './ChartLineChart';
import ChartPeriodSelector from './ChartPeriodSelector';
import VisuallyHiddenWrapper from './VisuallyHiddenWrapper';
import { DataStore, TranslationStore, AccessibilityStore } from '../stores';
import { AsAtDate } from '../models/configuration/AsAtDate';
import formatter from '../utils/formatter';
import HTag from './HTag';
import { ChartStore } from '../stores';
import Icon from './Icon';
import { Theme } from '../models/Theme';

const ChartWrapper = styled<any, any>('div', 'chartWrapper', 'chart')`
    ${propStyles};
`;

ChartWrapper.defaultProps = {
    marginY: 'md',
    overflow: 'hidden',
};

const CsvDownload = styled<any, any>('div', 'csvDownload', 'chart')`
    ${propStyles}
`;

CsvDownload.defaultProps = {
    width: '100%',
    textAlign: 'right',
    cursor: 'pointer',
};

const ChartContainer = styled<any, any>('div', 'chartContainer', 'chart')`
    ${propStyles};
`;

const ChartHeader = styled<any, any>('div', 'chartHeader', 'chart')`
    ${propStyles};
`;

ChartHeader.defaultProps = {
    display: 'flex',
    flexDirection: ['column', 'row'],
    justifyContent: ['center', 'space-between'],
    fontSize: 'xs',
};

const ChartControls = styled<any, any>('div', 'chartControls', 'chart')`
    ${propStyles};
`;

ChartControls.defaultProps = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: ['center', 'space-between'],
};

const ChartDatesWrapper = styled<any, any>('div', 'chartDatesWrapper', 'chart')`
    ${propStyles};
`;

ChartDatesWrapper.defaultProps = {
    display: 'flex',
};

const AsAtDate = styled<any, any>('div', 'asAtDate', 'chart')`
    ${propStyles};
`;

AsAtDate.defaultProps = {
    flex: '1 0 auto',
    fontSize: 'xs',
    textAlign: ['center', 'right'],
    marginX: 'sm',
    marginY: 'md',
};

interface ChartProps {
    theme?: Theme;
    accessibilityStore: AccessibilityStore;
    dataStore: DataStore;
    translationStore: TranslationStore;
    chartStore: ChartStore;
    showHeading?: boolean;
}

@inject('dataStore', 'translationStore', 'accessibilityStore')
@observer
class Chart extends Component<ChartProps> {
    constructor(props) {
        super(props);
    }

    public getThemeColor = (color: string) => {
        return get(this.props.theme, `color.${color}`, color);
    };

    get autocompleteThemeKeys() {
        return {
            dropdown: 'DateSelectorDropdown',
            dropdownList: 'DateSelectorList',
            dropdownListItem: 'DateSelectorListItem',
            input: 'DateSelectorInput',
            inputWrapper: 'DateSelectorInputWrapper',
            filterClearButton: 'DateSelectorFilterClearButton',
            filterClearButtonIcon: 'DateSelectorFilterClearButtonIcon',
            labelWrapper: 'DateSelectorLabel',
            label: 'DateSelectorLabelText',
            wrapper: 'DateSelectorWrapper',
        };
    }

    renderAccessibleTable = (
        data: Array<object>,
        columns?: Array<{ label: string; dataKey: string; format?: any }>
    ) => {
        const { accessibilityStore, chartStore } = this.props;
        if (accessibilityStore.hasAccessibility) {
            const {
                locale,
                selectedChartPeriod,
                startDate,
                endDate,
                chartConfig,
            } = chartStore;
            if (Array.isArray(data) && data.length > 0) {
                if (!columns) {
                    columns = Object.keys(data[0]).map(key => {
                        return { label: key, dataKey: key };
                    });
                }

                const summary = accessibilityStore.chartProperty(
                    chartConfig.id,
                    'summary',
                    {
                        title: accessibilityStore.chartProperty(
                            chartConfig.id,
                            'title'
                        ),
                        period:
                            (selectedChartPeriod &&
                                selectedChartPeriod.label.toLowerCase()) ||
                            'custom',
                        numberOfDataPoints: data.length || 0,
                        startDate: accessibilityStore.formatDate(
                            (selectedChartPeriod &&
                                selectedChartPeriod.startDate) ||
                                startDate,
                            locale
                        ),
                        endDate: accessibilityStore.formatDate(
                            (selectedChartPeriod &&
                                selectedChartPeriod.endDate) ||
                                endDate,
                            locale
                        ),
                    }
                );

                return (
                    <VisuallyHiddenWrapper>
                        <table summary={summary}>
                            <thead>
                                <tr>
                                    {columns.map((column, colId) => (
                                        <th
                                            key={`${chartConfig.id}-header-${colId}`}
                                            scope="col"
                                        >
                                            {column.label || column.dataKey}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((record, rowId) => {
                                    return (
                                        <tr
                                            key={`${chartConfig.id}-row-${rowId}`}
                                        >
                                            {columns.map((column, colId) => {
                                                column.format = column.format
                                                    ? {
                                                          ...column.format,
                                                          value:
                                                              record[
                                                                  column.dataKey
                                                              ],
                                                      }
                                                    : undefined;
                                                return (
                                                    <td
                                                        key={`${chartConfig.id}-row-${rowId}-cell-${colId}`}
                                                    >
                                                        {column.format
                                                            ? formatter(
                                                                  column.format
                                                              )
                                                            : record[
                                                                  column.dataKey
                                                              ]}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </VisuallyHiddenWrapper>
                );
            }
        }
    };

    public render() {
        const {
            theme,
            chartStore,
            showHeading,
            dataStore,
            accessibilityStore,
            ...props
        } = this.props;
        const { getThemeColor } = this;
        const {
            chartConfig,
            isMobile,
            data,
            config,
            mobileConfig,
            xAxis,
            yAxis,
            series,
            showBlankSeries,
            getLabelName,
            getSeriesName,
            getBaseFormatOptions,
            chartDisclaimers,
            displayDisclaimers,
            locale,
            fundCurrency,
            asAtDate,
            hasDateSelector,
            hasPeriodSelector,
            dateSelector,
            chartDatesStart,
            chartDatesEnd,
            startDate,
            endDate,
            setStartDate,
            setEndDate,
            minHeight,
            chartHasData,
            accessibleChartControlsLabel,
            queryContext,
            selectedChartPeriod,
            showCsvDownload,
            csvIconOptions,
            csvDownloadAlign,
            downloadCsv,
            hasPerformance,
        } = chartStore;

        return (
            <ChartWrapper
                id={`chart-${chartConfig.id}`}
                {...props}
                {...theme.ChartWrapper}
            >
                {showHeading && (
                    <HTag
                        componentTheme={'ChartHeading'}
                        defaultStyleProps={{
                            color: 'body',
                            fontFamily: 'heading',
                            fontWeight: 'bold',
                            fontSize: 'md',
                        }}
                        level={2}
                        text={chartConfig.label}
                    />
                )}
                {chartHasData && hasPerformance ? (
                    <Fragment>
                        {showCsvDownload && (
                            <CsvDownload
                                {...theme.CsvDownload}
                                textAlign={csvDownloadAlign}
                                onClick={downloadCsv}
                            >
                                {!csvIconOptions.hide &&
                                    csvIconOptions.position === 'before' && (
                                        <Icon
                                            fileName={csvIconOptions.filename}
                                        />
                                    )}
                                Download {chartConfig.label}
                                {!csvIconOptions.hide &&
                                    csvIconOptions.position === 'after' && (
                                        <Icon
                                            fileName={csvIconOptions.filename}
                                        />
                                    )}
                            </CsvDownload>
                        )}
                        <ChartContainer {...theme.ChartContainer}>
                            <ChartHeader {...theme.ChartHeader}>
                                <ChartControls
                                    {...theme.ChartControls}
                                    {...accessibleChartControlsLabel}
                                >
                                    {hasPeriodSelector && (
                                        <ChartPeriodSelector
                                            chartStore={chartStore}
                                        />
                                    )}
                                    {hasDateSelector && (
                                        <ChartDatesWrapper
                                            {...theme.ChartDatesWrapper}
                                        >
                                            <Autocomplete
                                                ref={
                                                    chartStore.startAutoCompleteRef
                                                }
                                                label={
                                                    dateSelector.startDateLabel
                                                }
                                                selectedItem={{
                                                    value: startDate,
                                                }}
                                                items={chartDatesStart.map(
                                                    date => ({ value: date })
                                                )}
                                                setSelectedItem={item =>
                                                    setStartDate(item.value)
                                                }
                                                valueFormat={
                                                    dateSelector.format
                                                }
                                                dataMetricIdPrefix={
                                                    'chartDateSelector'
                                                }
                                                themeKeys={
                                                    this.autocompleteThemeKeys
                                                }
                                            />
                                            <Autocomplete
                                                ref={
                                                    chartStore.endAutoCompleteRef
                                                }
                                                label={
                                                    dateSelector.endDateLabel
                                                }
                                                selectedItem={{
                                                    value: endDate,
                                                }}
                                                items={chartDatesEnd.map(
                                                    date => ({ value: date })
                                                )}
                                                setSelectedItem={item =>
                                                    setEndDate(item.value)
                                                }
                                                valueFormat={
                                                    dateSelector.format
                                                }
                                                defaultIndex={'last'}
                                                dataMetricIdPrefix={
                                                    'chartDateSelector'
                                                }
                                                themeKeys={
                                                    this.autocompleteThemeKeys
                                                }
                                            />
                                        </ChartDatesWrapper>
                                    )}
                                </ChartControls>
                                {asAtDate && (
                                    <AsAtDate {...theme.ChartAsAtDate}>
                                        <Translate>{asAtDate.label}</Translate>{' '}
                                        {asAtDate.value}
                                    </AsAtDate>
                                )}
                            </ChartHeader>
                            {chartConfig.type === 'BarChart' && (
                                <ChartBarChart
                                    config={config}
                                    mobileConfig={mobileConfig}
                                    data={data}
                                    xAxis={isMobile ? yAxis : xAxis}
                                    yAxis={isMobile ? xAxis : yAxis}
                                    layout={
                                        isMobile ? 'vertical' : 'horizontal'
                                    }
                                    minHeight={minHeight}
                                    aspect={isMobile ? undefined : 3}
                                    series={series}
                                    showBlankSeries={showBlankSeries}
                                    getThemeColor={getThemeColor}
                                    getLabelName={getLabelName}
                                    getSeriesName={getSeriesName}
                                    getBaseFormatOptions={getBaseFormatOptions}
                                    locale={locale}
                                    fundCurrency={fundCurrency}
                                    isMobile={isMobile}
                                    renderAccessibleTable={
                                        this.renderAccessibleTable
                                    }
                                />
                            )}
                            {chartConfig.type === 'LineChart' && (
                                <ChartLineChart
                                    config={config}
                                    data={data}
                                    xAxis={xAxis}
                                    yAxis={yAxis}
                                    series={series}
                                    getThemeColor={getThemeColor}
                                    getLabelName={getLabelName}
                                    getSeriesName={getSeriesName}
                                    getBaseFormatOptions={getBaseFormatOptions}
                                    locale={locale}
                                    fundCurrency={fundCurrency}
                                    renderAccessibleTable={
                                        this.renderAccessibleTable
                                    }
                                    queryContext={queryContext}
                                    aspect={isMobile ? undefined : 3}
                                    selectedChartPeriod={selectedChartPeriod}
                                />
                            )}
                        </ChartContainer>
                        <Disclaimers
                            marginX="0"
                            disclaimers={chartDisclaimers}
                            theme={{
                                ...theme,
                                DisclaimersList: theme.ChartDisclaimersList,
                                DisclaimerListItem:
                                    theme.ChartDisclaimerListItem,
                                DisclaimerCharacter:
                                    theme.ChartDisclaimerCharacter,
                                DisclaimerContent: theme.ChartDisclaimerContent,
                                DisclaimerReturnLink:
                                    theme.ChartDisclaimerReturnLink,
                            }}
                        />
                    </Fragment>
                ) : (
                    <Disclaimers
                        marginX="0"
                        disclaimers={displayDisclaimers}
                        theme={{
                            ...theme,
                            DisclaimersList: theme.ChartNoDataDisclaimersList,
                            DisclaimerListItem:
                                theme.ChartNoDataDisclaimerListItem,
                            DisclaimerCharacter:
                                theme.ChartNoDataDisclaimerCharacter,
                            DisclaimerContent:
                                theme.ChartNoDataDisclaimerContent,
                            DisclaimerReturnLink:
                                theme.ChartNoDataDisclaimerReturnLink,
                        }}
                    />
                )}
            </ChartWrapper>
        );
    }
}

export default withTheme(Chart) as any;



// WEBPACK FOOTER //
// ./src/Components/Chart.tsx